import { useState } from "react";
import { toast } from "sonner";
import { Loader2, ArrowRight, CheckCircle2, XCircle, RefreshCw, Handshake, FileText, ExternalLink, Wrench, Sparkles } from "lucide-react";
import { useTenant } from "../../contexts/TenantContext";
import { BrandButton } from "./TenantShell";
import { tapi, apiErr, detectHostTenant } from "../../lib/tenant";
import { PUBLIC_APP_URL, API } from "../../lib/api";

const ORIGIN = API.replace(/\/api$/, "");

const CONFIRM = "Your request has been sent. We will be in touch within two business days to facilitate the introduction.";

function Ring({ score, color }) {
  const r = 44, c = 2 * Math.PI * r, pct = Math.max(0, Math.min(100, score));
  return (
    <svg viewBox="0 0 100 100" className="h-28 w-28 shrink-0" data-testid="eco-score-ring">
      <circle cx="50" cy="50" r={r} fill="none" stroke="#E5E7EB" strokeWidth="8" />
      <circle cx="50" cy="50" r={r} fill="none" stroke={color || "var(--tenant)"} strokeWidth="8" strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={c * (1 - pct / 100)} transform="rotate(-90 50 50)" />
      <text x="50" y="55" textAnchor="middle" className="fill-neutral-900 text-2xl font-extrabold">{score}</text>
    </svg>
  );
}

function PartnerCard({ p, requested, onConnect }) {
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(!!requested);
  const connect = async () => {
    setBusy(true);
    try { await onConnect(p); setSent(true); } catch (_) { /* toast shown by caller */ }
    setBusy(false);
  };
  return (
    <li className="flex flex-col rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm" data-testid={`eco-partner-${p.partner_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-start gap-3">
          {p.logo_url && (
            <span className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-neutral-200 bg-white p-1">
              <img src={`${ORIGIN}${p.logo_url}`} alt={`${p.name} logo`} className="max-h-full max-w-full object-contain" data-testid={`eco-partner-logo-${p.partner_id}`} />
            </span>
          )}
          <div className="min-w-0">
          <p className="text-sm font-bold" data-testid={`eco-partner-name-${p.partner_id}`}>{p.name}</p>
          <span className="mt-1 inline-block rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white" style={{ background: "var(--tenant)" }}>{p.type_label}</span>
          </div>
        </div>
        {p.website && <a href={p.website} target="_blank" rel="noreferrer" className="text-neutral-400 hover:text-neutral-700" data-testid={`eco-partner-site-${p.partner_id}`}><ExternalLink size={14} /></a>}
      </div>
      <p className="mt-2 flex-1 text-xs text-neutral-600" data-testid={`eco-partner-blurb-${p.partner_id}`}>{p.blurb}</p>
      {sent ? (
        <p className="mt-3 flex items-start gap-2 rounded-xl bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700" data-testid={`eco-partner-sent-${p.partner_id}`}><CheckCircle2 size={14} className="mt-0.5 shrink-0" /> {CONFIRM}</p>
      ) : (
        <BrandButton onClick={connect} disabled={busy} className="mt-3 w-full !py-2 text-xs" data-testid={`eco-partner-connect-${p.partner_id}`}>
          {busy && <Loader2 size={13} className="animate-spin" />} Ask Clinq to connect me <ArrowRight size={14} />
        </BrandButton>
      )}
    </li>
  );
}

function StrongResults({ me, oc, onRefreshMe }) {
  const { sub, config } = useTenant();
  const requestedIds = new Set((me.partner_requests || []).map((r) => r.partner_id));
  const connect = async (p) => {
    try { await tapi.post(`/t/${sub}/partners/${p.partner_id}/connect`); toast.success("Request sent"); onRefreshMe(); }
    catch (e) { toast.error(apiErr(e, "Couldn't send your request")); throw e; }
  };
  const partners = oc.partners || [];
  return (
    <>
      <div className="flex flex-col items-center gap-5 rounded-2xl border border-neutral-200 bg-white p-6 text-center shadow-sm sm:flex-row sm:text-left">
        <Ring score={oc.score ?? 0} color={oc.band?.color} />
        <div className="flex-1">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: "var(--tenant)" }}>Capital Readiness Score · {oc.band?.label}</p>
          <h2 className="mt-1 text-xl font-extrabold" data-testid="eco-results-heading">Great news — your score qualifies you for introductions to {config.name}&apos;s ecosystem partners.</h2>
          <p className="mt-1 text-sm text-neutral-600">Based on your sector and the {oc.docs_count || 0} document{oc.docs_count === 1 ? "" : "s"} you submitted, here are the VCs, accelerators and funders we can connect you to. Request as many as you like.</p>
        </div>
      </div>
      {partners.length ? (
        <ul className="grid gap-4 sm:grid-cols-2" data-testid="eco-partner-list">
          {partners.map((p) => <PartnerCard key={p.partner_id} p={p} requested={requestedIds.has(p.partner_id)} onConnect={connect} />)}
        </ul>
      ) : (
        <p className="rounded-2xl border border-neutral-200 bg-white p-5 text-sm text-neutral-600" data-testid="eco-partner-empty">We&apos;re assembling the right partners for your sector — Clinq advisory will reach out with introductions.</p>
      )}
    </>
  );
}

function GapResults({ me, oc }) {
  const { sub } = useTenant();
  const [busy, setBusy] = useState(false);
  const [linkSentTo, setLinkSentTo] = useState("");
  const getReport = async () => {
    setBusy(true);
    try {
      const { data } = await tapi.post(`/t/${sub}/handoff`);
      if (data.mode === "auto_login") {
        const base = detectHostTenant() ? PUBLIC_APP_URL : window.location.origin;
        window.location.href = `${base}/handoff?token=${encodeURIComponent(data.token)}`;
        return;
      }
      setLinkSentTo(data.email || me.user.email);
      toast.success("Check your inbox for a secure sign-in link");
    } catch (e) { toast.error(apiErr(e, "Couldn't set up your Clinq account")); }
    setBusy(false);
  };
  return (
    <>
      <div className="flex flex-col items-center gap-5 rounded-2xl border border-neutral-200 bg-white p-6 text-center shadow-sm sm:flex-row sm:text-left">
        <Ring score={oc.score ?? 0} color={oc.band?.color} />
        <div className="flex-1">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: "var(--tenant)" }}>Capital Readiness Score · {oc.band?.label} · partners look for {oc.min_score}+</p>
          <h2 className="mt-1 text-xl font-extrabold" data-testid="eco-results-heading">You are almost there. Here is what you need to do to get connected with the partners in our ecosystem.</h2>
          <p className="mt-1 text-sm text-neutral-600">{oc.band?.interpretation}</p>
        </div>
      </div>
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5" data-testid="eco-gaps">
        <p className="flex items-center gap-2 text-sm font-bold text-amber-900"><Wrench size={15} /> Gaps in your Capital Readiness Score — and how to close them</p>
        <ol className="mt-3 space-y-2">
          {(oc.gaps || []).map((g, i) => (
            <li key={i} className="rounded-xl bg-white p-3" data-testid={`eco-gap-${i}`}>
              <p className="text-sm font-bold text-neutral-900">{g.label}</p>
              <p className="mt-0.5 text-xs text-neutral-600">{g.action}</p>
            </li>
          ))}
        </ol>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl text-white" style={{ background: "var(--tenant)" }}><Sparkles size={18} /></span>
          <div>
            <p className="text-sm font-bold">Get the full, verified picture</p>
            <p className="text-xs text-neutral-500">Your Capital Readiness Report on clinq.africa shows exactly what funders will see, with a step-by-step plan to close every gap.</p>
          </div>
        </div>
        <BrandButton onClick={getReport} disabled={busy} data-testid="eco-get-report-btn">{busy && <Loader2 size={14} className="animate-spin" />} Get my Capital Readiness Report <ArrowRight size={16} /></BrandButton>
      </div>
      {linkSentTo && <p className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700" data-testid="eco-handoff-email-sent"><CheckCircle2 size={14} /> You already have a Clinq account — we&apos;ve emailed a secure sign-in link to {linkSentTo}.</p>}
    </>
  );
}

export const StepEcoResults = ({ me, onRefreshMe, onResubmit }) => {
  const oc = me.outcome || {};
  return (
    <div className="space-y-5" data-testid="tenant-step-eco-results" data-strong={oc.strong ? "1" : "0"}>
      {oc.strong ? <StrongResults me={me} oc={oc} onRefreshMe={onRefreshMe} /> : <GapResults me={me} oc={oc} />}

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="rounded-2xl border border-neutral-200 bg-white p-4" data-testid="eco-results-docs">
          <p className="flex items-center gap-2 text-sm font-bold"><FileText size={14} /> Your documents</p>
          <ul className="mt-2 space-y-1 text-xs">
            {(oc.have_docs || []).map((d) => <li key={d.id} className="flex items-center gap-2 text-emerald-700"><CheckCircle2 size={13} /> {d.label}</li>)}
            {(oc.missing_docs || []).map((d) => <li key={d.id} className="flex items-center gap-2 text-neutral-500"><XCircle size={13} /> {d.label} <span className="text-neutral-400">— not provided</span></li>)}
          </ul>
        </div>
        <div className="rounded-2xl border border-neutral-200 bg-white p-4" data-testid="eco-results-signals">
          <p className="text-sm font-bold">What we read from your documents</p>
          {(oc.signals || []).length ? (
            <ul className="mt-2 space-y-1 text-xs">
              {oc.signals.map((s) => <li key={s.label} className={"flex items-center gap-2 " + (s.positive ? "text-emerald-700" : "text-amber-700")}>{s.positive ? <CheckCircle2 size={13} /> : <XCircle size={13} />} {s.label}: {s.value}</li>)}
            </ul>
          ) : <p className="mt-2 text-xs text-neutral-500">Upload management accounts, a financial model or bank statements so we can read your numbers.</p>}
        </div>
      </div>

      <div className="flex items-center justify-between rounded-2xl border border-neutral-200 bg-white p-4">
        <p className="flex items-center gap-2 text-xs text-neutral-500"><Handshake size={14} /> Added new documents? Resubmit to refresh your score and matches.</p>
        <button type="button" onClick={onResubmit} className="inline-flex items-center gap-1 text-xs font-semibold text-neutral-600 hover:text-neutral-900" data-testid="eco-results-resubmit"><RefreshCw size={13} /> Add documents &amp; resubmit</button>
      </div>
    </div>
  );
};
